import axios from "axios";
import { useCallback } from "react";
import { REACT_APP_ACR_API_BASE_URL, REACT_APP_ACR_API_PROTOCOL } from "../utlities/core.constants";

// The API validates acrType against a fixed enum of short codes rather than accepting the
// full descriptive text. Keep the codes and their display labels together so the dropdown can
// show the friendly text while sending/receiving the value the API actually expects.
export const ACR_TYPE_OPTIONS = [
  { value: "VPAT_508", label: "VPAT® 2.4 508: Revised Section 508 Edition (WCAG 2.0)" },
  { value: "VPAT_508_WCAG", label: "VPAT® 2.4 508 + WCAG: Revised Section 508 Edition (WCAG 2.1)" },
  { value: "VPAT_WCAG", label: "VPAT® 2.4 WCAG: WCAG 2.1" },
];

// The API matches acrType case-insensitively; accept either the short code or (for hyperlinks
// saved before this migration) the old descriptive text so both display correctly.
export function getAcrTypeLabel(acrType) {
  const normalized = String(acrType || "").trim().toLowerCase();
  if (!normalized) return "";
  const match = ACR_TYPE_OPTIONS.find(
    (option) => option.value.toLowerCase() === normalized || option.label.toLowerCase() === normalized
  );
  return match?.label || acrType;
}

export default function useHyperlink() {
  const submitHyperlink = useCallback(async (hyperlink) => {
    const response = axios.post(`${REACT_APP_ACR_API_PROTOCOL}://${REACT_APP_ACR_API_BASE_URL}/api/hyperlink`, hyperlink);

    return response;
  }, []);

  const getHyperlinkByProductId = useCallback(async (productId) => {
    const response = await axios.get(
      `${REACT_APP_ACR_API_PROTOCOL}://${REACT_APP_ACR_API_BASE_URL}/api/hyperlink?productId=${productId}`
    );
    // App axios validateStatus often allows 4xx without throwing — 404 means no hyperlink (e.g. ACR Editor–only product).
    if (response.status === 404) {
      return null;
    }
    if (response.status < 200 || response.status >= 300) {
      const err = new Error("Unable to load hyperlink for product");
      err.response = response;
      throw err;
    }
    return response.data;
  }, []);

  const getHyperlinkById = useCallback(async (hyperlinkId) => {
    const response = axios.get(`${REACT_APP_ACR_API_PROTOCOL}://${REACT_APP_ACR_API_BASE_URL}/api/hyperlink?id=${hyperlinkId}`);

    return (await response).data;
  }, []);

  const getAllHyperlinks = useCallback(async () => {
    const response = await axios.get(`${REACT_APP_ACR_API_PROTOCOL}://${REACT_APP_ACR_API_BASE_URL}/api/hyperlink/all`);
    return response.data;
  }, []);

  const updateHyperlink = useCallback(async (hyperlinkId, hyperlink) => {
    const response = await axios.patch(`${REACT_APP_ACR_API_PROTOCOL}://${REACT_APP_ACR_API_BASE_URL}/api/hyperlink/${hyperlinkId}`, hyperlink)
    return response;
  }, []);

  const fetchAcrTypes = useCallback(async () => {
    return ACR_TYPE_OPTIONS;
  }, []);

  return { submitHyperlink, fetchAcrTypes, getHyperlinkByProductId, getHyperlinkById, getAllHyperlinks, updateHyperlink }
}
